import { useSelector } from 'react-redux';
import { get } from 'lodash';

import { FIELD_PATHS, FIELD_VALUE_LABELS, FREQUENCY, LIABILITY_TYPE, LOAN_OPTION } from '@funnels/constants';
import { Asset, FormValuesALIEO, Liability, ReduxStoreData } from '@funnels/types';
import { convert, getApplicantsFullnameFromOwners } from '@funnels/utils';

import {
  AssetDescriptionReadOnlyView,
  CurrentInterestRateReadOnlyView,
  FinancialInstitutionReadOnlyView,
  LiabilityCreditLimitReadOnlyView,
  LiabilityOwnershipReadOnlyView,
  LiabilityTypeReadOnlyView,
  LoanOptionReadOnlyView,
  NewLimitReadOnlyView,
  OutstandingBalanceReadOnlyView,
  RemainingLoanTermReadOnlyView,
  RepaymentsAmountReadOnly,
  RepaymentsFrequencyReadOnly,
} from '../../../Questions';
import Table from '../../../Table';

export const HEADERS = [
  { text: 'Category', min: 220, max: 320 },
  { text: 'Owner(s)', min: 220, max: 320 },
  { text: 'Institution', min: 220, max: 320 },
  { text: 'Credit limit', min: 170, max: 220 },
  { text: 'Balance', min: 170, max: 220 },
  { text: 'Rate', min: 130, max: 180 },
  { text: 'Payment', min: 170, max: 220 },
  { text: 'Frequency', min: 170, max: 220 },
  { text: 'Action', min: 220, max: 320 },
  { text: 'Ongoing Limit', min: 170, max: 220 },
  { text: 'Remaining term', min: 320, max: 350 },
  { text: 'Description', min: 220, max: 320 },
];

function mapState(currentState: ReduxStoreData) {
  const data: FormValuesALIEO = {
    applicants: currentState.applicants.data,
    assets: currentState.assets.data,
    liabilities: currentState.liabilities.data,
  };
  const error = currentState.applicants.error || currentState.assets.error || currentState.liabilities.error;
  const loading = currentState.applicants.loading + currentState.assets.loading + currentState.liabilities.loading;

  return { data, error, loading };
}

function LiabilityTable() {
  const { data } = useSelector(mapState);
  const applicants = get(data, FIELD_PATHS.APPLICANTS(), []);
  const assets = get(data, FIELD_PATHS.ASSETS(), []);
  const liabilities = get(data, FIELD_PATHS.LIABILITIES(), []);

  return (
    <Table title="Liabilities" headers={HEADERS}>
      {liabilities.length > 0 && (
        <tbody data-component="table-tbody">
          {liabilities.map((liability: Liability, index: number) => {
            const type = get({ liabilities }, FIELD_PATHS.LIABILITY_TYPE(index));
            let category = get(FIELD_VALUE_LABELS, type, type);

            if (type === LIABILITY_TYPE.CAR_LOAN) {
              const assetIndex = assets.findIndex((asset: Asset) => {
                return liability.assetClientId.indexOf(asset.clientId) !== -1;
              });

              if (assetIndex !== -1) {
                const vehicleMake = get({ assets }, FIELD_PATHS.ASSET_VEHICLE_MAKE(assetIndex));

                if (vehicleMake) {
                  category += ` - ${vehicleMake}`;
                }
              }
            }

            const owners = get({ liabilities }, FIELD_PATHS.LIABILITY_OWNERS(index));
            const ownersFullName = getApplicantsFullnameFromOwners(owners, applicants);
            const financialInstitution = get({ liabilities }, FIELD_PATHS.LIABILITY_FINANCIAL_INSTITUTION(index));
            const creditLimit = get({ liabilities }, FIELD_PATHS.LIABILITY_CREDIT_LIMIT(index));
            const outstandingBalance = get({ liabilities }, FIELD_PATHS.LIABILITY_OUTSTANDING_BALANCE(index));
            const interestRate = get({ liabilities }, FIELD_PATHS.LIABILITY_INTEREST_RATE(index));
            const repaymentsAmount = get({ liabilities }, FIELD_PATHS.LIABILITY_REPAYMENT_VALUE(index));
            const repaymentsFrequency = get({ liabilities }, FIELD_PATHS.LIABILITY_REPAYMENT_FREQUENCY(index));
            const clearFromThisLoan = get({ liabilities }, FIELD_PATHS.LIABILITY_CLEARING_FROM_THIS_LOAN(index));
            const clearFromOtherSource = get({ liabilities }, FIELD_PATHS.LIABILITY_CLEARING_FROM_OTHER_SOURCE(index));
            const isTopup = get({ liabilities }, FIELD_PATHS.LIABILITY_TO_BE_TOPPED_UP(index));

            const desc = get({ liabilities }, FIELD_PATHS.LIABILITY_DESCRIPTION(index));
            let loanOption;

            if (clearFromThisLoan) {
              loanOption = LOAN_OPTION.CONSOLIDATE;
            } else if (clearFromOtherSource) {
              loanOption = LOAN_OPTION.CLEAR_FROM_OTHER_SOURCE;
            } else if (clearFromThisLoan === false && !clearFromOtherSource) {
              loanOption = LOAN_OPTION.NONE;
            }
            if (isTopup && !clearFromThisLoan) loanOption = LOAN_OPTION.INCREASE;

            const newLimit = get({ liabilities }, FIELD_PATHS.LIABILITY_NEW_LIMIT(index));
            const remainingTerm = get({ liabilities }, FIELD_PATHS.LIABILITY_REMAINING_TERM(index));
            const monthlyRepaymentsAmount =
              repaymentsFrequency === FREQUENCY.MONTHLY
                ? repaymentsAmount
                : convert(repaymentsAmount, repaymentsFrequency, FREQUENCY.MONTHLY);

            // vanityRepaymentsFrequency - "vanity" because this is what we are displaying to the front end;
            const vanityRepaymentsFrequency = monthlyRepaymentsAmount ? FREQUENCY.MONTHLY : '--';

            return (
              <tr key={liability.clientId}>
                <td>
                  <LiabilityTypeReadOnlyView value={category} textSize="md" />
                </td>
                <td>
                  <LiabilityOwnershipReadOnlyView value={ownersFullName} textSize="md" />
                </td>
                <td>
                  <FinancialInstitutionReadOnlyView value={financialInstitution} textSize="md" />
                </td>
                <td>
                  <LiabilityCreditLimitReadOnlyView value={creditLimit} textSize="md" />
                </td>
                <td>
                  <OutstandingBalanceReadOnlyView value={outstandingBalance} textSize="md" />
                </td>
                <td>
                  <CurrentInterestRateReadOnlyView value={interestRate} textSize="md" />
                </td>
                <td>
                  <RepaymentsAmountReadOnly value={monthlyRepaymentsAmount} textSize="md" />
                </td>
                <td>
                  <RepaymentsFrequencyReadOnly value={vanityRepaymentsFrequency} textSize="md" />
                </td>
                <td>
                  <LoanOptionReadOnlyView value={loanOption} textSize="md" />
                </td>
                <td>
                  <NewLimitReadOnlyView value={newLimit} textSize="md" />
                </td>
                <td>
                  <RemainingLoanTermReadOnlyView value={remainingTerm} textSize="md" />
                </td>
                <td>
                  <AssetDescriptionReadOnlyView value={desc} textSize="md" />
                </td>
              </tr>
            );
          })}
        </tbody>
      )}
    </Table>
  );
}

export default LiabilityTable;
